import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayISO = () => new Date().toISOString().split("T")[0];

const fmtDuration = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? `${min % 60}` : ""}` : `${min} min`);
const fmtPrice = (p) => (p == null ? "sob consulta" : `${Number(p).toFixed(0)} €`);

export const Booking = () => {
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_id: "", staff: "Indiferente", notes: "" });
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [closed, setClosed] = useState(false);
  const [cutoffMsg, setCutoffMsg] = useState("");
  const [showPrices, setShowPrices] = useState(true);

  useEffect(() => {
    axios.get(`${API}/booking-services`).then((res) => {
      setServices(res.data.services);
      setShowPrices(res.data.show_prices !== false);
    });
    axios.get(`${API}/booking-staff`).then((res) => setStaffList(res.data.staff));
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const name = p.get("nome");
    const email = p.get("email");
    const phone = p.get("tel");
    if (name || email || phone) {
      setForm((f) => ({ ...f, name: name || f.name, email: email || f.email, phone: phone || f.phone }));
      setTimeout(() => {
        const el = document.getElementById("marcacoes");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }, []);

  const selectedService = services.find((s) => s.id === form.service_id);
  const categories = [...new Set(services.map((s) => s.category || "Outros"))];

  useEffect(() => {
    if (!date || !form.service_id) return;
    setSelectedTime("");
    setLoadingSlots(true);
    axios
      .get(`${API}/bookings/availability`, { params: { date, service_id: form.service_id, staff: form.staff } })
      .then((res) => {
        setSlots(res.data.slots);
        setClosed(!!res.data.closed);
        if (res.data.cutoff_passed) {
          setCutoffMsg(res.data.cutoff_message || "O prazo para agendar este dia já terminou.");
          toast.warning(res.data.cutoff_message || "Prazo de agendamento terminado para esta data.", { duration: 6000 });
        } else {
          setCutoffMsg("");
        }
      })
      .catch((err) => {
        setSlots([]);
        setClosed(false);
        setCutoffMsg("");
        toast.error(err.response?.data?.detail || "Data indisponível.");
      })
      .finally(() => setLoadingSlots(false));
  }, [date, form.service_id, form.staff]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_id) return toast.error("Escolha um serviço.");
    if (!date || !selectedTime) return toast.error("Escolha a data e o horário.");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, { ...form, date, time: selectedTime });
      setConfirmed(res.data);
      toast.success("Marcação confirmada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível concluir a marcação.");
      if (err.response?.status === 409) {
        const res = await axios.get(`${API}/bookings/availability`, {
          params: { date, service_id: form.service_id, staff: form.staff },
        });
        setSlots(res.data.slots);
        setSelectedTime("");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <section id="marcacoes" data-testid="booking-section" className="bg-[#FAFAFA] py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="label-editorial text-clay mb-6">Marcação confirmada</p>
          <h2 data-testid="booking-confirmation" className="font-display text-4xl md:text-6xl tracking-tight leading-tight">
            Até já, <span className="italic text-clay">{confirmed.name.split(" ")[0]}</span>.
          </h2>
          <p className="mt-8 text-black/60 font-light text-lg">
            {confirmed.service} com {confirmed.staff}, {confirmed.date} às {confirmed.time} ({fmtDuration(confirmed.duration_min)}).
            <br />
            Rua da Quinta, lote 109, RC-B Urb. St. Clara, Leiria.
          </p>
          <button
            data-testid="booking-new-btn"
            onClick={() => {
              setConfirmed(null);
              setForm({ name: "", email: "", phone: "", service_id: "", staff: "Indiferente", notes: "" });
              setDate("");
              setSelectedTime("");
            }}
            className="mt-10 label-editorial border border-black/20 px-8 py-4 hover:border-black transition-colors duration-300"
          >
            Nova marcação
          </button>
        </div>
      </section>
    );
  }

  const inputCls =
    "w-full bg-white border border-black/15 px-4 py-3.5 text-sm font-light focus:outline-none focus:border-black transition-colors duration-300 placeholder:text-black/30";

  return (
    <section id="marcacoes" data-testid="booking-section" className="bg-[#FAFAFA] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-4"
        >
          <p className="label-editorial text-clay mb-6">Marcações</p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-none">
            Agende a sua <span className="italic text-clay">consulta</span>
          </h2>
          <p className="mt-8 text-black/55 font-light leading-relaxed">
            Escolha o serviço, o dia e o horário. O aconselhamento inicial é
            personalizado e gratuito.
          </p>
          <p className="mt-6 label-editorial text-black/40">
            Segunda a Sábado
            <br />
            9h00 – 13h00 · 14h30 – 20h00
          </p>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          onSubmit={handleSubmit}
          data-testid="booking-form"
          className="lg:col-span-8 bg-white border border-black/5 p-8 md:p-12 space-y-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label-editorial text-black/50 block mb-3">Nome</label>
              <input
                data-testid="booking-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="O seu nome"
                className={inputCls}
              />
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-3">Email</label>
              <input
                data-testid="booking-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@exemplo.pt"
                className={inputCls}
              />
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-3">Telemóvel</label>
              <input
                data-testid="booking-phone-input"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="9XX XXX XXX"
                className={inputCls}
              />
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-3">Serviço</label>
              <select
                data-testid="booking-service-select"
                required
                value={form.service_id}
                onChange={(e) => setForm({ ...form, service_id: e.target.value })}
                className={inputCls}
              >
                <option value="">Escolha um serviço</option>
                {categories.map((cat) => (
                  <optgroup key={cat} label={cat}>
                    {services
                      .filter((s) => (s.category || "Outros") === cat)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name} · {fmtDuration(s.duration_min)}{showPrices ? ` · ${fmtPrice(s.price)}` : ""}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="label-editorial text-black/50 block mb-3">Profissional</label>
              <select
                data-testid="booking-staff-select"
                value={form.staff}
                onChange={(e) => setForm({ ...form, staff: e.target.value })}
                className={inputCls}
              >
                <option value="Indiferente">Indiferente</option>
                {staffList.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label-editorial text-black/50 block mb-3">Data</label>
            <input
              data-testid="booking-date-input"
              required
              type="date"
              min={todayISO()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inputCls}
            />
            {selectedService && (
              <p data-testid="booking-service-summary" className="mt-3 text-xs text-black/40 font-light tracking-wide">
                {selectedService.name} · duração {fmtDuration(selectedService.duration_min)}{showPrices ? ` · ${fmtPrice(selectedService.price)}` : ""}
              </p>
            )}
          </div>

          {date && form.service_id && (
            <div data-testid="booking-slots">
              <label className="label-editorial text-black/50 block mb-3">Horário</label>
              {loadingSlots ? (
                <p className="text-sm text-black/40 font-light">A carregar horários…</p>
              ) : cutoffMsg ? (
                <div data-testid="booking-cutoff-warning" className="border-l-2 border-amber-500 bg-amber-50/60 px-4 py-3.5 text-sm text-amber-900 font-light">
                  <p className="mb-1"><strong className="font-normal">Prazo terminado.</strong></p>
                  <p className="text-amber-800/90">{cutoffMsg}</p>
                </div>
              ) : closed ? (
                <p className="text-sm text-black/40 font-light">Encerrado neste dia. Escolha outra data.</p>
              ) : slots.length === 0 ? (
                <p className="text-sm text-black/40 font-light">Sem horários para esta data.</p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {slots.map((slot) => (
                    <button
                      key={slot.time}
                      type="button"
                      data-testid={`slot-${slot.time.replace(":", "-")}`}
                      disabled={!slot.available}
                      onClick={() => setSelectedTime(slot.time)}
                      className={`py-2.5 text-xs tracking-widest border transition-colors duration-200 ${
                        selectedTime === slot.time
                          ? "bg-clay text-white border-clay"
                          : slot.available
                          ? "border-black/15 hover:border-clay"
                          : "border-black/5 text-black/20 line-through cursor-not-allowed"
                      }`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="label-editorial text-black/50 block mb-3">Notas (opcional)</label>
            <textarea
              data-testid="booking-notes-input"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Alguma preferência ou algo que queira partilhar?"
              className={inputCls}
            />
          </div>

          <button
            data-testid="booking-submit-btn"
            type="submit"
            disabled={submitting}
            className="label-editorial bg-clay text-white px-10 py-4 hover:bg-clay-dark transition-colors duration-300 disabled:opacity-50"
          >
            {submitting ? "A confirmar…" : "Confirmar Marcação"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
